/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import SafeComponent from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import {
  getLocator,
  getIconPath,
  getBrandInCaps,
  createUUID,
  parseBoolean,
  getAspectRatio,
} from '@tcp/core/src/utils';
import { CAROUSAL_ICON } from '@tcp/core/src/constants/brandSpecific.constants';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Button from '@tcp/core/src/components/common/atoms/Button';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import {
  GLOBAL_ACCESSIBILITY_LABELS,
  GlobalAccessibilityLabels,
} from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import CTAItems from '@tcp/core/src/components/common/molecules/ModuleContainer/molecules/CTAItems';
import ImageAnimationModule from '@tcp/core/src/components/common/molecules/ImageAnimation';
import ANALYTICS_CONSTANTS from '@tcp/web/src/constants/AnalyticsConstants';
import ImageComponentStyle, {
  Carousel,
  StyledImage,
  DamImage,
  StyledSlideCTAItems,
  StyledCTASlideContainer,
} from '../styles/ImageComponent.style';
import { MEDIA_BORDER_RADIUS_OVERRIDE_MAP } from '../../../constants/ModuleContainer.constants';

import config from '../config';
import {
  getClassName,
  getMediaBorderRadiusOverride,
  getScrollIdentifier,
  getScrollIdentifierID,
  getSearchCTA,
  imgVideoData,
  getModuleQVCheck,
  renderDifferentImageOnMobile,
} from './imageComponentHelper';

const CTAComp = ({ ctaItems, styleOverrides }) => {
  return (
    <StyledSlideCTAItems>
      <CTAItems
        ctaItems={ctaItems}
        isHpNewDesignCTAEnabled
        isHomePage
        styleOverrides={styleOverrides}
      />
    </StyledSlideCTAItems>
  );
};

// Carousel Next and Prev Arrows
const CarouselArrow = ({ arrowType, label, onClick }) => {
  return (
    <button
      type="button"
      tabIndex="0"
      aria-label={label}
      data-locator={`moduleContainer_${arrowType}_arrow`}
      className={`carousel-nav-arrow-button carousel-nav-${arrowType}-button`}
      onClick={onClick}
    >
      <svg width="15" height="52" role="img">
        <path
          fill="#1A1A1A"
          d="M.113 50.539a1 1 0 001.774.923l13-25a1 1 0 000-.923l-13-25a1 1 0 10-1.774.923L12.873 26 .113 50.54z"
        />
      </svg>
    </button>
  );
};

CarouselArrow.propTypes = {
  arrowType: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClick: PropTypes.func,
};

const getDisableVideosOnAppleValue = (
  disableVideosForAppleDevice,
  disableVideosForAppleDeviceValue
) => disableVideosForAppleDevice || disableVideosForAppleDeviceValue;

/* eslint-disable complexity, sonarjs/cognitive-complexity */
const getImage = (
  {
    image,
    imageMobile,
    video,
    videoMobile,
    singleCTAButton,
    mediaBorderRadiusOverride,
    disableVideosForAppleDevice,
    slotIndex,
    isOnScrollAnimationEnabled,
    assignKeyValue,
    isEnableMute,
    transformedCTAItemsComposite,
    styleOverrides,
    useRouterNavigation,
    moduleAnalyticsTag,
  },
  IMG_DATA,
  carouselCtaType,
  index,
  isOnlyImage,
  isPreload
) => {
  const { imgData, videoData, imgMData, videoMData } = imgVideoData({
    image,
    mediaBorderRadiusOverride,
    video,
    imageMobile,
    videoMobile,
  });

  const { extraConfig } = React.useContext(LabelsContext);
  const { isDamImageV2Enabled, disableVideosForAppleDeviceValue } = extraConfig || {};
  const { url, text, target, action } = singleCTAButton || {};
  const { isModuleQVEnabled, moduleQVProductId } = getModuleQVCheck(target, action);
  const showDifferentImageOnMobile = !!imageMobile;
  const showVideoOnMobile = !!videoMobile;
  const { onscroll_animation: onScrollAnimation = false } = !isEmpty(videoData) && videoData;
  const isOnScrolledChecked = parseBoolean(onScrollAnimation);
  const isOnScrollAnimation = getScrollIdentifier(isOnScrollAnimationEnabled, isOnScrolledChecked);
  const onScrollIdentifier = getScrollIdentifierID(
    isOnScrollAnimation,
    videoData,
    videoMobile,
    slotIndex
  );
  const isSearchsingleCTAButton = url?.split('?')[0]?.split('/');
  const isSearchPage = getSearchCTA(isSearchsingleCTAButton);
  const aspectRatioObj = getAspectRatio(image, video);
  const slideCTAItems =
    !isEmpty(transformedCTAItemsComposite) && transformedCTAItemsComposite[`slide${index + 1}`];
  const StyledImageComp = (
    <StyledImage
      id={onScrollIdentifier}
      onScrollIdentifier={onScrollIdentifier}
      className={getClassName(isOnlyImage)}
      aspectRatioObj={aspectRatioObj}
    >
      <ClickTracker
        clickData={{
          customEvents: ['event100', 'event20'],
          var22: 'internal search',
          pageSearchText:
            isSearchPage && isSearchsingleCTAButton[2] && isSearchsingleCTAButton[2].length > 0
              ? `${isSearchsingleCTAButton[2]?.replace('%20', ' ')}`
              : '',
          pageSearchType: isSearchPage ? 'banner search' : '',
          listingCount: 4,
          searchType: isSearchPage ? ANALYTICS_CONSTANTS.bannerSearchClick : '',
        }}
      >
        {renderDifferentImageOnMobile(showDifferentImageOnMobile, showVideoOnMobile) ? (
          <DamImage
            imgData={imgMData}
            dataLocator={`${getLocator('moduleContainer_carousel_image_img')}${index + 1}`}
            className="module-container-img-full-width"
            link={singleCTAButton}
            isOptimizedVideo
            isHomePage
            lazyLoad
            isModule
            key={`damImage-${createUUID()}`}
            disableVideosForAppleDevice={getDisableVideosOnAppleValue(
              disableVideosForAppleDevice,
              disableVideosForAppleDeviceValue
            )}
            preloadImage={isPreload}
            aspectRatioObj={aspectRatioObj}
            isDamImageV2Enabled={isDamImageV2Enabled}
            assignKeyValue={assignKeyValue}
            isEnableMute={isEnableMute}
            useRouterNavigation={useRouterNavigation}
            moduleAnalyticsTag={moduleAnalyticsTag}
            isModuleQVEnabled={isModuleQVEnabled}
            moduleQVProductId={moduleQVProductId}
          />
        ) : (
          <DamImage
            imgData={imgData}
            dataLocator={`${getLocator('moduleContainer_carousel_image_img')}${index + 1}`}
            className="module-container-img-full-width"
            link={singleCTAButton}
            videoData={videoData}
            showVideoOnMobile={showVideoOnMobile}
            videoDataMobile={videoMData}
            isOptimizedVideo
            isHomePage
            lazyLoad
            isModule
            disableVideosForAppleDevice={getDisableVideosOnAppleValue(
              disableVideosForAppleDevice,
              disableVideosForAppleDeviceValue
            )}
            onScrollIdentifier={onScrollIdentifier}
            preloadImage={isPreload}
            aspectRatioObj={aspectRatioObj}
            isDamImageV2Enabled={isDamImageV2Enabled}
            assignKeyValue={assignKeyValue}
            isEnableMute={isEnableMute}
            useRouterNavigation={useRouterNavigation}
            moduleAnalyticsTag={moduleAnalyticsTag}
            isModuleQVEnabled={isModuleQVEnabled}
            moduleQVProductId={moduleQVProductId}
          />
        )}
        {carouselCtaType === 'link' && singleCTAButton && (
          <Anchor
            dataLocator={`${getLocator('moduleContainer_carousel_image_link_cta')}${index + 1}`}
            className="carousel-cta-link"
            url={url}
            title={text}
            target={target}
            underline
            anchorVariation="primary"
            fontSizeVariation="large"
            moduleAnalyticsTag={moduleAnalyticsTag}
          >
            {text}
          </Anchor>
        )}
      </ClickTracker>
    </StyledImage>
  );

  if (!isEmpty(slideCTAItems)) {
    return (
      <StyledCTASlideContainer>
        {StyledImageComp}
        {
          <CTAComp
            ctaItems={slideCTAItems}
            styleOverrides={styleOverrides}
            moduleAnalyticsTag={moduleAnalyticsTag}
          />
        }
      </StyledCTASlideContainer>
    );
  }

  return StyledImageComp;
};

const containsSlides = (ctaItemsComposite) => {
  return Object.keys(ctaItemsComposite).some((key) => key !== 'none');
};

const getModuleContainerCarousel = (
  useRouterNavigation,
  largeCompImageSimpleCarousel,
  isPreload,
  CAROUSEL_OPTIONS,
  carouselConfig,
  IMG_DATA,
  carouselCtaType,
  mediaBorderRadiusOverride,
  {
    disableVideosForAppleDevice,
    slotIndex,
    mouseMoved,
    colName,
    isOnScrollAnimationEnabled,
    carouselPadding,
    assignKeyValue,
    isEnableMute,
    transformedCTAItemsComposite,
    styleOverrides,
    moduleAnalyticsTag,
  }
) => {
  const hasSlides =
    !isEmpty(transformedCTAItemsComposite) && containsSlides(transformedCTAItemsComposite);
  return (
    <Carousel
      options={CAROUSEL_OPTIONS}
      carouselConfig={carouselConfig}
      isModule
      carouselPadding={carouselPadding}
      hasSlides={hasSlides}
    >
      {largeCompImageSimpleCarousel.map(({ image, video, singleCTAButton }, index) => {
        return getImage(
          {
            image,
            video,
            singleCTAButton,
            mediaBorderRadiusOverride,
            disableVideosForAppleDevice,
            slotIndex,
            mouseMoved,
            colName,
            isOnScrollAnimationEnabled,
            assignKeyValue,
            isEnableMute,
            transformedCTAItemsComposite,
            styleOverrides,
            useRouterNavigation,
            moduleAnalyticsTag,
          },
          IMG_DATA,
          carouselCtaType,
          index,
          false,
          isPreload
        );
      })}
    </Carousel>
  );
};

const getCarouselOrImage = (
  useRouterNavigation,
  largeCompImageSimpleCarousel,
  isPreload,
  CAROUSEL_OPTIONS,
  carouselConfig,
  IMG_DATA,
  carouselCtaType,
  mediaBorderRadiusOverride,
  {
    disableVideosForAppleDevice,
    slotIndex,
    mouseMoved,
    colName,
    isOnScrollAnimationEnabled,
    carouselPadding,
    animationSpeedOption,
    assignKeyValue,
    isEnableMute,
    transformedCTAItemsComposite,
    styleOverrides,
    moduleAnalyticsTag,
  }
) => {
  const { image, imageMobile, video, videoMobile, singleCTAButton } =
    (largeCompImageSimpleCarousel && largeCompImageSimpleCarousel[0]) || {};
  const { infiniteImageScroll } = !isEmpty(image) ? image : '0';
  const isInfiniteImageScrollEnabled = parseBoolean(infiniteImageScroll);
  if (isInfiniteImageScrollEnabled) {
    const aspectRatioObj = getAspectRatio(image);

    return (
      <ImageAnimationModule
        imageData={largeCompImageSimpleCarousel}
        animationSpeedProp={animationSpeedOption}
        aspectRatioObj={aspectRatioObj}
        isPreload={isPreload}
        assignKeyValue={assignKeyValue}
      />
    );
  }

  return largeCompImageSimpleCarousel?.length > 1
    ? getModuleContainerCarousel(
        useRouterNavigation,
        largeCompImageSimpleCarousel,
        isPreload,
        CAROUSEL_OPTIONS,
        carouselConfig,
        IMG_DATA,
        carouselCtaType,
        mediaBorderRadiusOverride,
        {
          disableVideosForAppleDevice,
          slotIndex,
          mouseMoved,
          colName,
          isOnScrollAnimationEnabled,
          carouselPadding,
          assignKeyValue,
          isEnableMute,
          transformedCTAItemsComposite,
          styleOverrides,
          moduleAnalyticsTag,
        }
      )
    : getImage(
        {
          useRouterNavigation,
          image,
          imageMobile,
          video,
          videoMobile,
          singleCTAButton,
          mediaBorderRadiusOverride,
          disableVideosForAppleDevice,
          slotIndex,
          mouseMoved,
          colName,
          isOnScrollAnimationEnabled,
          assignKeyValue,
          isEnableMute,
          transformedCTAItemsComposite,
          styleOverrides,
          moduleAnalyticsTag,
        },
        IMG_DATA,
        carouselCtaType,
        0,
        true,
        isPreload
      );
};

const ImageComponent = (props) => {
  const {
    largeCompImageSimpleCarousel,
    className,
    styleOverrides,
    isHpNewModuleDesignEnabled,
    isOnScrollAnimationEnabled,
    colName,
    disableVideosForAppleDevice,
    slotIndex,
    mouseMoved,
    isPreload,
    assignKeyValue,
    transformedCTAItemsComposite,
    useRouterNavigation,
    moduleAnalyticsTag,
  } = props;
  const accessibilityLabels = getLabelFromCopyOrFromTranslation(
    GLOBAL_ACCESSIBILITY_LABELS,
    GlobalAccessibilityLabels
  );

  const playIconButton = renderLabel(accessibilityLabels.playIconButton, 'playIconButton');
  const pauseIconButton = renderLabel(accessibilityLabels.pauseIconButton, 'pauseIconButton');
  const previousButton = renderLabel(accessibilityLabels.previousButton, 'previousButton');
  const nextIconButton = renderLabel(accessibilityLabels.nextIconButton, 'nextIconButton');
  const carouselCtaType = largeCompImageSimpleCarousel && largeCompImageSimpleCarousel[0].ctaType;
  const { IMG_DATA, CAROUSEL_OPTIONS } = config;
  const brand = getBrandInCaps();
  const carouselIcon = CAROUSAL_ICON[brand];
  const [curCarouselSlideIndex, setCurCarouselSlideIndex] = useState(0);
  const sliderFadeOptions = styleOverrides['slider-fade'];
  const animationSpeedOption = styleOverrides['animation-speed'];
  const sliderSpeedOption = styleOverrides['slider-speed'];
  const mediaBorderRadiusOverride =
    isHpNewModuleDesignEnabled && !isEmpty(styleOverrides)
      ? getMediaBorderRadiusOverride(
          isHpNewModuleDesignEnabled,
          styleOverrides,
          MEDIA_BORDER_RADIUS_OVERRIDE_MAP[colName]
        )
      : {};
  const { enableMute: { enableMute: isEnableMute = false } = {} } = styleOverrides || {};
  const carouselPadding = styleOverrides['carousel-padding'];
  const carouselConfig = {
    autoplay: true,
    dataLocatorPlay: getLocator('moduleContainer_play_button'),
    dataLocatorPause: getLocator('moduleContainer_pause_button'),
    customArrowLeft: getIconPath(carouselIcon),
    customArrowRight: getIconPath(carouselIcon),
    dataLocatorCarousel: 'carousel_banner',
  };

  CAROUSEL_OPTIONS.prevArrow = <CarouselArrow arrowType="prev" label={previousButton} />;
  CAROUSEL_OPTIONS.nextArrow = <CarouselArrow arrowType="next" label={nextIconButton} />;
  carouselConfig.autoplay = carouselConfig.autoplay && largeCompImageSimpleCarousel?.length > 1;
  carouselConfig.pauseIconButtonLabel = pauseIconButton;
  carouselConfig.playIconButtonLabel = playIconButton;

  CAROUSEL_OPTIONS.afterChange = (index) => {
    setCurCarouselSlideIndex(index);
  };

  const overrideCarouselOptions = !isEmpty(sliderFadeOptions)
    ? {
        fade: true,
        cssEase: 'linear',
        infinite: true,
        autoplaySpeed: parseFloat(sliderFadeOptions?.speed),
        speed: 50,
      }
    : {};

  const sliderSpeedOptions = !isEmpty(sliderSpeedOption)
    ? {
        overrideSpeed: true,
        autoplaySpeed: parseFloat(sliderSpeedOption?.speed),
      }
    : {};

  return (
    <Row fullBleed className={`${className} carousel-image-layout`} key={assignKeyValue}>
      <Col
        colSize={{
          small: 6,
          medium: 8,
          large: 12,
        }}
      >
        {getCarouselOrImage(
          useRouterNavigation,
          largeCompImageSimpleCarousel,
          isPreload,
          { ...CAROUSEL_OPTIONS, ...overrideCarouselOptions, ...sliderSpeedOptions },
          carouselConfig,
          IMG_DATA,
          carouselCtaType,
          mediaBorderRadiusOverride,
          {
            disableVideosForAppleDevice,
            slotIndex,
            mouseMoved,
            colName,
            isOnScrollAnimationEnabled,
            animationSpeedOption,
            carouselPadding,
            assignKeyValue,
            isEnableMute,
            transformedCTAItemsComposite,
            styleOverrides,
            moduleAnalyticsTag,
          }
        )}
        {carouselCtaType === 'button' &&
          largeCompImageSimpleCarousel[curCarouselSlideIndex].singleCTAButton && (
            <Row
              fullBleed={{
                small: true,
                medium: true,
                large: true,
              }}
            >
              <Col
                colSize={{
                  small: 6,
                  medium: 8,
                  large: 12,
                }}
              >
                <Button
                  buttonVariation="fixed-width"
                  dataLocator={`${getLocator(
                    'moduleContainer_carousel_image_button_cta'
                  )}${curCarouselSlideIndex}`}
                  className="carousel-cta-button"
                  cta={largeCompImageSimpleCarousel[curCarouselSlideIndex].singleCTAButton}
                >
                  {largeCompImageSimpleCarousel[curCarouselSlideIndex].singleCTAButton.text}
                </Button>
              </Col>
            </Row>
          )}
      </Col>
    </Row>
  );
};

ImageComponent.propTypes = {
  largeCompImageSimpleCarousel: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isHpNewModuleDesignEnabled: PropTypes.bool.isRequired,
  styleOverrides: PropTypes.shape({}),
  className: PropTypes.string,
  disableVideosForAppleDevice: PropTypes.bool.isRequired,
  slotIndex: PropTypes.number.isRequired,
  mouseMoved: PropTypes.bool.isRequired,
  isPreload: PropTypes.bool,
  useRouterNavigation: PropTypes.bool,
};

ImageComponent.defaultProps = {
  styleOverrides: {},
  className: '',
  isPreload: false,
  useRouterNavigation: false,
};

export default withStyles(SafeComponent(ImageComponent), ImageComponentStyle);
export { ImageComponent as ImageComponentVanilla };
